import { isMobile } from 'react-device-detect'
import * as THREE from 'three'
import Controls from './Controls'
import { BaseTweener } from '../../scripts/TweenService'

class TweenEuler extends BaseTweener<THREE.Euler> { // TODO : if other people are happy with this implementation, we can move it to TweenService file
  protected cachedEular = new THREE.Euler()

  protected ProcessValue (_startValue: THREE.Euler, _endValue: THREE.Euler, _position: number): THREE.Euler {
    return this.cachedEular.set(
      THREE.MathUtils.lerp(_startValue.x, _endValue.x, _position),
      THREE.MathUtils.lerp(_startValue.y, _endValue.y, _position),
      THREE.MathUtils.lerp(_startValue.z, _endValue.z, _position)
    )
  }
}

class SpringRotationTween extends TweenEuler {
  protected cachedEular = new THREE.Euler()
  public SpringAmount = 1.5
  // because this is hack, we are caching the delta time here TODO : potentially refactor tween so that detlaTime is expsoed to the process value meothod
  public cachedDeltaTime = 0
  public threshold = 0.01

  public Start () : void {
    super.Start()
    this.cachedEular.copy(this.startValue)
  }

  protected ProcessValue (_startValue: THREE.Euler, _endValue: THREE.Euler, _position: number): THREE.Euler {
    this.progress = 0 // we not replying on the lerping for this tween twpe
    const lerpAmount = this.cachedDeltaTime * this.SpringAmount
    if (Math.abs(this.cachedEular.x - _endValue.x) < this.threshold &&
        Math.abs(this.cachedEular.y - _endValue.y) < this.threshold &&
        Math.abs(this.cachedEular.z - _endValue.z) < this.threshold
    ) {
      this.progress = this.duration + 1 // this will trigger the tweener to end
    }

    return this.cachedEular.set(
      THREE.MathUtils.lerp(this.cachedEular.x, _endValue.x, lerpAmount),
      THREE.MathUtils.lerp(this.cachedEular.y, _endValue.y, lerpAmount),
      THREE.MathUtils.lerp(this.cachedEular.z, _endValue.z, lerpAmount)
    )
  }
}

interface PanningCameraParams {
  canvas: HTMLCanvasElement,
  degreeOfRange?: number,
  turnLimits?: THREE.Vector2,
  dragSensitivity?: number,
  dragLimitDampening?: number,
  timeout?: number,
  freeMovement?: boolean,
  startRotX?: number
  startRotY?: number
  flipY?: boolean
  flipX?: boolean,
  yAxisRotationTarget: THREE.Object3D,
  xAxisRotationTarget: THREE.Object3D
  }

export default class PanningCameraBehaviour extends Controls {
  protected readonly yRotateAxis: THREE.Object3D
  protected readonly xRotateAxis: THREE.Object3D

  // @ts-ignore
  private turnLimits: THREE.Vector2
  private readonly dragSensitivity: number
  private triggeredRecenter = false

  private initialForwardRotation!: THREE.Vector3 | null

  private panningOffset: THREE.Vector3

  private currentCachedPointerDelta: THREE.Vector2 = new THREE.Vector2()

  private previousTouchPosition: THREE.Vector2 = new THREE.Vector2()
  private touchCachedDeltaPosition: THREE.Vector2 = new THREE.Vector2()

  private pointerDragStarted = false
  private mouseStartPosition: THREE.Vector2 = new THREE.Vector2(0, 0)

  private degreeOfRange: number

  private enabled = false
  public get isEnabled () : boolean {
    return this.enabled
  }

  private canvas: HTMLCanvasElement

  private flipY: boolean = false
  private flipX: boolean = false

  private resetTween : SpringRotationTween

  constructor (
    _params: PanningCameraParams
  ) {
    super()

    this.panningOffset = new THREE.Vector3()

    this.xRotateAxis = _params.xAxisRotationTarget
    this.yRotateAxis = _params.yAxisRotationTarget

    _params = _params as PanningCameraParams

    this.degreeOfRange = _params.degreeOfRange ?? 90

    if (_params.turnLimits) this.turnLimits = _params.turnLimits
    else {
      this.turnLimits = new THREE.Vector2(THREE.MathUtils.degToRad(this.degreeOfRange / 2), THREE.MathUtils.degToRad(this.degreeOfRange / 2))
    }

    this.flipY = _params.flipY ?? false
    this.flipX = _params.flipX ?? false
    this.dragSensitivity = _params.dragSensitivity ?? 1.6
    this.canvas = _params.canvas

    this.resetTween = new SpringRotationTween({
      duration: 2,
      StartValue: new THREE.Euler(0, 0, 0),
      EndValue: new THREE.Euler(0, 0, 0),
      OnEnd: () => {
        this.triggeredRecenter = false
      }
    })

    document.addEventListener('RoomModelAnimationPlay', () => {
      this.triggeredRecenter = true
      this.resetTween.startValue = new THREE.Euler(
        this.panningOffset.x,
        this.panningOffset.y,
        0
      )
      this.resetTween.Start()
    })
  }

  get ratio (): number {
    return window.innerWidth / window.innerHeight
  }

  public dispose (): void {
    this.deactivatePointerEvents()
    this.enabled = false
  }

  private active = false

  public deactivate (): void { if (this.active) this.deactivatePointerEvents() }

  public activate (): void { if (!this.active) this.activatePointerEvents() }

  public enable (): void {
    this.enabled = true

    this.setupCameraPosition()
    this.activatePointerEvents()

    this.setupParents()
  }

  onMouseDown = (_event: MouseEvent) => {
    if (this.triggeredRecenter) return
    this.pointerDragStarted = true
    this.mouseStartPosition.x = _event.clientX
    this.mouseStartPosition.y = _event.clientY
  }

  onMouseMove = (_event: MouseEvent) => {
    if (!this.pointerDragStarted) return

    this.currentCachedPointerDelta = new THREE.Vector2(
      _event.movementX,
      _event.movementY
    )
  }

  onMouseUp = (_event: MouseEvent) => {
    this.pointerDragStarted = false
  }

  onTouchStart = (_event: TouchEvent) => {
    if (this.triggeredRecenter) return
    this.pointerDragStarted = true
    this.currentCachedPointerDelta = new THREE.Vector2(0, 0)
    this.previousTouchPosition.set(
      _event.touches[0].screenX,
      _event.touches[0].screenY
    )
    this.mouseStartPosition.x = (_event.changedTouches[0].pageX)
    this.mouseStartPosition.y = (_event.changedTouches[0].pageY)
  }

  onTouchMove = (_event: TouchEvent) => {
    this.touchCachedDeltaPosition.set(
      _event.touches[0].screenX - this.previousTouchPosition.x,
      _event.touches[0].screenY - this.previousTouchPosition.y
    )

    this.previousTouchPosition.set(
      _event.touches[0].screenX,
      _event.touches[0].screenY
    )
  }

  onTouchEnd = (_event: TouchEvent) => {
    this.currentCachedPointerDelta = new THREE.Vector2(0, 0)
    if (isMobile) {
      this.previousTouchPosition.set(_event.changedTouches[0].screenX, _event.changedTouches[0].screenY)
    } else {
      this.previousTouchPosition.set(_event.touches[0].screenX, _event.touches[0].screenY)
    }
  }

  public onUpdate (_deltaTime:number): void {
    if (this.triggeredRecenter === true) {
      this.applyRecenterTween(_deltaTime)
    }
    this.rotate(_deltaTime)
  }

  private applyRecenterTween (_deltaTime:number) {
    if (this.resetTween.playing === false) return
    this.resetTween.cachedDeltaTime = _deltaTime
    this.resetTween.Update(_deltaTime)
    const currentResetRotation = this.resetTween.GetValue()
    this.panningOffset.set(
      currentResetRotation.x,
      currentResetRotation.y,
      0)
  }

  private rotate (_deltaTime:number): void {
    const mouseDelta = (isMobile)
      ? this.touchCachedDeltaPosition
      : this.currentCachedPointerDelta

    // get it normalized. both x and y need to by divied by width, so that they can stay consistent
    mouseDelta.x /= window.innerWidth
    mouseDelta.y /= window.innerWidth

    const currentSensitivity = this.dragSensitivity

    const x = mouseDelta.y * currentSensitivity * (this.flipX ? 1 : -1)
    const y = mouseDelta.x * currentSensitivity * (this.flipY ? -1 : 1)

    this.panningOffset.add(new THREE.Vector3(-x, y, 0))

    if (this.initialForwardRotation) {
      const newDir = this.sumVectors([
        this.initialForwardRotation,
        this.panningOffset
      ])

      this.yRotateAxis.rotation.set(0, newDir.y, 0)
      this.xRotateAxis.rotation.set(newDir.x, 0, 0)
    }
  }

  private setupParents () {
    this.yRotateAxis.position.set(0, 0, 0)
    this.yRotateAxis.rotation.set(0, 0, 0)
    this.xRotateAxis.position.set(0, 0, 0)
    this.xRotateAxis.rotation.set(0, 0, 0)
  }

  private setupCameraPosition (): void {
    if (!this.initialForwardRotation) this.initialForwardRotation = new THREE.Vector3()
  }

  private activatePointerEvents (): void {
    if (!isMobile) {
      this.canvas.addEventListener('mousedown', this.onMouseDown)
      this.canvas.addEventListener('mousemove', this.onMouseMove)
      this.canvas.addEventListener('mouseup', this.onMouseUp)
      this.canvas.addEventListener('mouseleave', this.onMouseUp)
    }

    if (window.Touch) {
      this.canvas.addEventListener('touchstart', this.onTouchStart)
      this.canvas.addEventListener('touchmove', this.onTouchMove)
      this.canvas.addEventListener('touchend', this.onTouchEnd)
    }

    this.active = true
  }

  private deactivatePointerEvents (): void {
    if (!isMobile) {
      this.canvas.removeEventListener('mousedown', this.onMouseDown)
      this.canvas.removeEventListener('mousemove', this.onMouseMove)
      this.canvas.removeEventListener('mouseup', this.onMouseUp)
      this.canvas.removeEventListener('mouseleave', this.onMouseUp)
    }

    if (window.Touch) {
      this.canvas.removeEventListener('touchstart', this.onTouchStart)
      this.canvas.removeEventListener('touchmove', this.onTouchMove)
      this.canvas.removeEventListener('touchend', this.onTouchEnd)
    }

    this.active = false
  }
}
